
import { Transaction } from '@/types';

/**
 * Gera um UUID válido para transações
 */
export const generateTransactionId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  
  // Fallback para ambientes que não suportam crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c == 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

/**
 * Gera um UUID válido para lançamentos futuros
 */
export const generateFutureReleaseId = (): string => {
  return generateTransactionId();
};

/**
 * Valida se um ID é um UUID válido
 */
export const isValidUUID = (id: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(id);
};

/**
 * Mapeia status do frontend para o banco de dados
 */
export const mapStatusToDatabase = (status: 'paid' | 'pending' | 'partial'): string => {
  switch (status) {
    case 'paid':
      return 'efetivada';
    case 'pending':
    case 'partial':
      return 'pendente';
    default:
      return 'efetivada';
  }
};

/**
 * Mapeia status do banco de dados para o frontend
 */
export const mapStatusFromDatabase = (status: string): 'paid' | 'pending' | 'partial' => {
  switch (status) {
    case 'efetivada':
      return 'paid';
    case 'pendente':
      return 'pending';
    default:
      return 'paid';
  }
};

/**
 * Valida dados essenciais da transação antes da criação
 */
export const validateTransactionData = (data: any): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!data.description || data.description.trim() === '') {
    errors.push('Descrição é obrigatória');
  }
  
  // FIXED: Better amount validation that handles different number formats
  const amount = Number(data.amount);
  if (data.amount === undefined || data.amount === null || isNaN(amount) || amount === 0) {
    errors.push('Valor deve ser maior que zero');
  }
  
  if (!data.type || !['receita', 'despesa'].includes(data.type)) {
    errors.push('Tipo deve ser receita ou despesa');
  }
  
  if (!data.date) {
    errors.push('Data é obrigatória');
  }
  
  if (!data.categoryId) {
    errors.push('Categoria é obrigatória');
  }
  
  if (!data.paymentMethod) {
    errors.push('Meio de pagamento é obrigatório');
  }
  
  // Validações específicas por meio de pagamento
  if (data.paymentMethod === 'cartao' && data.type === 'despesa' && !data.cardId) {
    errors.push('Cartão é obrigatório para despesas com cartão de crédito');
  }
  
  if (data.status !== 'pending' && data.paymentMethod !== 'cartao' && !data.accountId) {
    errors.push('Conta é obrigatória para este meio de pagamento');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Limpa e normaliza dados da transação
 */
export const normalizeTransactionData = (data: any): any => {
  // FIXED: Better amount normalization that preserves the original value if valid
  let normalizedAmount = data.amount;
  if (data.amount !== undefined && data.amount !== null) {
    const numericAmount = Number(data.amount);
    if (!isNaN(numericAmount)) {
      normalizedAmount = numericAmount;
    }
  }
  
  // FIXED: Handle status correctly - if it's an object with _type undefined, extract value or default
  let normalizedStatus = 'paid'; // default
  if (data.status && typeof data.status === 'string') {
    normalizedStatus = data.status;
  } else if (data.status && typeof data.status === 'object' && data.status.value && data.status.value !== 'undefined') {
    normalizedStatus = data.status.value;
  }
  
  // FIXED: Normalize UUID fields - convert empty strings to null
  const normalizeUUID = (value: any) => {
    if (!value || value === '' || value === 'undefined') return null;
    return value;
  };
  
  return {
    ...data,
    description: data.description?.trim() || '',
    amount: normalizedAmount,
    installmentNumber: data.installmentNumber || 1,
    installments: data.installments || 1,
    status: normalizedStatus,
    // Normalize UUID fields to prevent empty string errors
    categoryId: normalizeUUID(data.categoryId),
    cardId: normalizeUUID(data.cardId),
    accountId: normalizeUUID(data.accountId),
    parentTransactionId: normalizeUUID(data.parentTransactionId),
    originalTransactionId: normalizeUUID(data.originalTransactionId),
    // Campos FIFO - preservar se fornecidos
    originalAmount: data.originalAmount,
    paidPortion: data.paidPortion,
  };
};
